import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Lightbulb, Mail, AlertTriangle, Lock, CheckCircle, Inbox } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err) {
      // Même si l'email n'existe pas, on affiche un message de succès pour la sécurité
      // (ne pas révéler si un email est enregistré ou non)
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-60 h-60 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-16">
            <img 
              src="/logo.png" 
              alt="DJAMANN" 
              className="w-16 h-16 rounded-2xl shadow-2xl object-contain bg-white/10 backdrop-blur-sm p-1"
            />
            <div>
              <h1 className="text-3xl font-bold text-white">DJAMANN</h1>
              <p className="text-white/70 text-sm">Lancez votre business, même avec zéro franc</p>
            </div>
          </div>

          {/* Main Heading */}
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
              Pas de panique ! <br />
              <span className="text-yellow-200">On vous aide</span>
            </h2>
            <p className="text-white/80 text-lg max-w-md">
              Ça arrive à tout le monde d'oublier son mot de passe. Entrez votre email et nous vous enverrons un lien pour le réinitialiser.
            </p>
          </div>

          {/* Tips */}
          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Lightbulb size={16} /> Conseil sécurité
              </h3>
              <p className="text-white/70 text-sm">
                Utilisez un mot de passe unique d'au moins 8 caractères avec des lettres, chiffres et symboles.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Mail size={16} /> Vérifiez vos spams
              </h3>
              <p className="text-white/70 text-sm">
                Si vous ne recevez pas l'email, pensez à vérifier votre dossier spam ou courrier indésirable.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-white/60 text-sm">
            © 2025 DJAMANN. La plateforme qui démocratise l'entrepreneuriat.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-gray-50 px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logo.png" 
              alt="DJAMANN" 
              className="w-20 h-20 mx-auto mb-4 rounded-2xl shadow-lg object-contain"
            />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
              DJAMANN
            </h1>
          </div>

          {!success ? (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Lock size={32} />
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Mot de passe oublié ?</h2>
                <p className="text-gray-500">
                  Entrez votre email et nous vous enverrons un lien de réinitialisation
                </p>
              </div>

              {/* Form */}
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail size={16} className="inline mr-1" /> Adresse email
                    </label>
                    <input
                      type="email"
                      placeholder="nom@exemple.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all bg-gray-50 hover:bg-white"
                    />
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-3">
                      <AlertTriangle size={20} />
                      <span className="text-sm font-medium">{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl shadow-lg shadow-amber-200 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-3">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Envoi en cours...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Envoyer le lien
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </span>
                    )}
                  </button>
                </form>

                {/* Divider */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-400">ou</span>
                  </div>
                </div>

                {/* Back to Login */}
                <Link
                  to="/login"
                  className="w-full py-3.5 border-2 border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Retour à la connexion
                </Link>
              </div>
            </>
          ) : (
            /* Success Message */
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Email envoyé !</h2>
              <p className="text-gray-500 mb-8 max-w-sm mx-auto">
                Si un compte existe avec l'adresse <strong className="text-gray-700">{email}</strong>, 
                vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8 text-left">
                <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <Inbox size={16} /> Vous n'avez pas reçu l'email ?
                </h3>
                <ul className="text-amber-700 text-sm space-y-2">
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Vérifiez votre dossier spam/courrier indésirable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Assurez-vous d'avoir entré la bonne adresse email</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>•</span>
                    <span>Attendez quelques minutes, l'email peut prendre du temps</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => { setSuccess(false); setEmail(''); }}
                  className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all"
                >
                  Essayer avec une autre adresse
                </button>
                <Link
                  to="/login"
                  className="w-full py-3.5 border-2 border-gray-200 text-gray-600 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Retour à la connexion
                </Link>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              Besoin d'aide ? Consultez nos{' '}
              <Link to="/terms" className="text-amber-600 hover:underline">conditions d'utilisation</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
